import React, { useState, useEffect, useCallback, useRef } from "react";
import { Table, Button, Form, Modal, InputGroup, Spinner, Alert, Card } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt, faEdit, faBan } from "@fortawesome/free-solid-svg-icons";
import { client } from "../../api/client";

export function BlocageBouleTable({ targetLevel, title, description, triggerNewBlockage, mode = 'blocage' }) {
    const [rules, setRules] = useState([]);
    const [targets, setTargets] = useState([]); 
    const [draws, setDraws] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [validationError, setValidationError] = useState(null);
    
    // Modal state
    const [openModal, setOpenModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedNumbers, setSelectedNumbers] = useState([]);
    const manualInputRef = useRef(null);
    
    const DEFAULT_CONFIG = {
        target_level: targetLevel === 'TIRAGE' ? 'GENERAL' : targetLevel,
        agent: '', branch: '',
        draw: '', 
        game_type: 'BORLETTE',
        number: '',
        limit_amount: mode === 'blocage' ? '0' : '',
        is_blocked: mode === 'blocage',
        is_active: true,
    };
    
    const [formData, setFormData] = useState(DEFAULT_CONFIG);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const apiTargetLevel = targetLevel === 'TIRAGE' ? 'GENERAL' : targetLevel;
            const promises = [
                client(`risk/number-overrides/?target_level=${apiTargetLevel}`),
                client("draw-configs/") // To get draws
            ];

            if (targetLevel === 'AGENT') {
                promises.push(client("agents/")); 
            } else if (targetLevel === 'BRANCH') {
                promises.push(client("branches/"));
            }

            const results = await Promise.all(promises);
            let rulesList = results[0]?.results || results[0] || [];
            
            if (targetLevel === 'GENERAL') {
                rulesList = rulesList.filter(r => !r.draw);
            } else if (targetLevel === 'TIRAGE') {
                rulesList = rulesList.filter(r => !!r.draw);
            }

            if (mode === 'blocage') {
                rulesList = rulesList.filter(r => r.is_blocked === true);
            } else {
                rulesList = rulesList.filter(r => r.is_blocked === false);
            }

            setRules(rulesList);
            
            const rawDraws = Array.isArray(results[1]) ? results[1] : (results[1]?.results || results[1]?.draws || []);
            setDraws(rawDraws);

            if (targetLevel !== 'TENANT' && targetLevel !== 'GENERAL' && targetLevel !== 'TIRAGE') {
                setTargets(Array.isArray(results[2]) ? results[2] : (results[2]?.results || []));
            }
            setFetchError(null);
        } catch (e) { 
            console.error(e); 
            setFetchError(e?.message || e?.detail || "Erreur de chargement des données API.");
        }
        setLoading(false);
    }, [targetLevel]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (triggerNewBlockage > 0) {
            handleOpenModal();
        }
    }, [triggerNewBlockage]);

    const handleOpenModal = (rule = null) => {
        if (rule) {
            setFormData({ ...rule, draw: rule.draw || '' });
            setIsEditing(true);
            setSelectedNumbers([rule.number]);
        } else {
            const firstDrawId = draws.length > 0 ? (draws[0].draw?.id || draws[0].id || '') : '';
            setFormData({ 
                ...DEFAULT_CONFIG, 
                draw: targetLevel === 'TIRAGE' ? firstDrawId : '' 
            });
            setIsEditing(false);
            setSelectedNumbers([]);
        }
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (field === 'game_type') {
            setSelectedNumbers([]);
            setMariageStep(1);
            setMariageFirst(null);
        }
    };

    // ─── Mariage combo builder ────────────────────────────────────
    const [mariageStep, setMariageStep] = useState(1); 
    const [mariageFirst, setMariageFirst] = useState(null);

    const handleMariagePick = (num) => {
        const s = num.toString().padStart(2, '0');
        if (mariageStep === 1) {
            setMariageFirst(s);
            setMariageStep(2);
        } else {
            const combo = mariageFirst + '-' + s;
            setSelectedNumbers(prev => {
                if (prev.includes(combo)) return prev; 
                return [...prev, combo];
            });
            setMariageStep(1);
            setMariageFirst(null);
        }
    };

    const cancelMariageStep = () => {
        setMariageStep(1);
        setMariageFirst(null);
    };

    const formatMariageDisplay = (combo) => {
        if (!combo || combo.length !== 5) return combo;
        return `${combo.slice(0, 2)} × ${combo.slice(3, 5)}`;
    };

    const toggleNumber = (num) => {
        const s = num.toString().padStart(2, '0');
        setSelectedNumbers(prev => 
            prev.includes(s) ? prev.filter(n => n !== s) : [...prev, s]
        );
    };

    const quickSelect = (type) => {
        const numbers = [];
        for (let i = 0; i < 100; i++) {
            const s = i.toString().padStart(2, '0');
            if (type === 'all') numbers.push(s);
            if (type === 'doubles' && i % 11 === 0) numbers.push(s); 
            if (type === 'even' && i % 2 === 0) numbers.push(s);     
            if (type === 'odd' && i % 2 !== 0) numbers.push(s);
            if (type.startsWith('end-')) {
                const end = type.split('-')[1];
                if (s.endsWith(end)) numbers.push(s);
            }
            if (type.startsWith('start-')) {
                const start = type.split('-')[1];
                if (s.startsWith(start)) numbers.push(s);
            }
        }
        setSelectedNumbers(numbers);
    };

    const handleAddManualNumbers = () => {
        const input = manualInputRef.current;
        if (!input) return;
        const val = input.value.trim();
        if (!val) return;
        const nums = val.split(/[,\s]+/)
            .map(n => n.trim())
            .filter(n => n.length > 0)
            .map(n => {
                if (formData.game_type !== 'MARIAGE') {
                    return n.replace(/[^0-9]/g, '');
                }
                return n;
            });
        setSelectedNumbers(prev => [...new Set([...prev, ...nums])]);
        input.value = '';
    };

    const saveRule = async () => {
        if (targetLevel === 'AGENT' && !formData.agent && !isEditing) return alert("Veuillez sélectionner un agent");
        if (targetLevel === 'BRANCH' && !formData.branch && !isEditing) return alert("Veuillez sélectionner une succursale");
        if (targetLevel === 'TIRAGE' && !formData.draw) return alert("Veuillez sélectionner un tirage");
        if (selectedNumbers.length === 0) return alert("Veuillez sélectionner au moins un numéro");
        
        setSaving(true);
        setValidationError(null);
        try {
            const basePayload = { ...formData };
            if (targetLevel !== 'AGENT') basePayload.agent = null;
            if (targetLevel !== 'BRANCH') basePayload.branch = null;
            if (basePayload.draw === '') basePayload.draw = null;
            
            if (!basePayload.limit_amount || basePayload.limit_amount === '') {
                basePayload.limit_amount = '0';
            }

            if (isEditing) {
                await client(`risk/number-overrides/${basePayload.id}/`, { 
                    method: "PUT", 
                    body: { ...basePayload, number: selectedNumbers[0] } 
                });
            } else {
                await client(`risk/number-overrides/batch/`, { 
                    method: "POST", 
                    body: { 
                        numbers: selectedNumbers,
                        data: basePayload
                    } 
                });
            }
            await load();
            handleCloseModal();
        } catch (e) {
            console.error(e);
            setValidationError(e?.detail || e?.message || "Une erreur est survenue lors de l'enregistrement.");
        }
        setSaving(false);
    };

    const deleteRule = async (id) => {
        if (!window.confirm("Supprimer ce blocage ?")) return;
        
        setSaving(true);
        try {
            await client(`risk/number-overrides/${id}/`, { method: "DELETE" });
            await load();
        } catch (e) { 
            console.error(e);
            alert("Erreur lors de la suppression");
        }
        setSaving(false);
    };
    
    const toggleStatus = async (rule) => {
        try {
            await client(`risk/number-overrides/${rule.id}/`, { 
                method: "PUT", 
                body: { ...rule, is_active: !rule.is_active } 
            });
            setRules(prev => prev.map(r => r.id === rule.id ? { ...r, is_active: !rule.is_active } : r));
        } catch (e) { 
            console.error(e); 
            alert("Erreur lors du changement de statut");
        }
    };

    const getTargetName = (rule) => {
        if (targetLevel === 'GENERAL' || targetLevel === 'TIRAGE') return "Général";
        
        let targetId = targetLevel === 'AGENT' ? rule.agent : rule.branch;
        const targetObj = targets.find(t => t.id === targetId);
        
        if (!targetObj) return rule.agent_name || rule.branch_name || `ID: ${targetId}`;

        if (targetLevel === 'AGENT') return targetObj.username || `${targetObj.first_name} ${targetObj.last_name}`;
        return targetObj.name;
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center p-5">
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (fetchError) {
        return (
            <Alert variant="danger" className="text-center p-4">
                <h6 className="fw-bold mb-2">Erreur Opérationnelle</h6>
                <p className="small mb-3">{fetchError}</p>
                <Button onClick={load} variant="outline-danger" size="sm">Réessayer</Button>
            </Alert>
        );
    }

    return (
        <div>
            <div className="table-responsive p-3">
                <Table bordered hover className="align-middle mb-0" style={{ borderColor: "#DDD" }}>
                    <thead>
                        <tr style={{ backgroundColor: "#F8F9FA" }}>
                            <th style={{ fontSize: "0.9rem", fontWeight: "bold" }}>Cible</th>
                            <th style={{ fontSize: "0.9rem", fontWeight: "bold" }}>Tirage</th>
                            <th style={{ fontSize: "0.9rem", fontWeight: "bold" }}>Type</th>
                            <th style={{ fontSize: "0.9rem", fontWeight: "bold" }}>Numéro</th>
                            {mode === 'limite' && <th style={{ fontSize: "0.9rem", fontWeight: "bold" }} className="text-end">Limite</th>}
                            <th style={{ fontSize: "0.9rem", fontWeight: "bold" }}>Statut</th>
                            <th style={{ fontSize: "0.9rem", fontWeight: "bold", width: "100px" }} className="text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rules.map((rule) => (
                            <tr key={rule.id}>
                                <td className="fw-bold" style={{ color: "#222", fontSize: "0.9rem" }}>
                                    {getTargetName(rule)}
                                </td>
                                <td style={{ color: "#444", fontSize: "0.9rem" }}>
                                    {rule.draw_name || "Tous"}
                                </td>
                                <td style={{ color: "#444", fontSize: "0.9rem" }}>
                                    {rule.game_type_display || rule.game_type}
                                </td>
                                <td className="fw-bold" style={{ color: "#222", fontSize: "0.9rem" }}>
                                    {rule.number}
                                </td>
                                {mode === 'limite' && (
                                    <td style={{ color: "#444", fontSize: "0.9rem" }} className="text-end">
                                        {rule.is_blocked ? "—" : `${Number(rule.limit_amount).toLocaleString()} HTG`}
                                    </td>
                                )}
                                <td style={{ color: "#444", fontSize: "0.9rem" }}>
                                    <div className="d-flex align-items-center">
                                        <Form.Check 
                                            type="switch"
                                            id={`switch-${rule.id}`}
                                            checked={rule.is_active}
                                            onChange={() => toggleStatus(rule)}
                                            style={{ cursor: 'pointer', margin: 0 }}
                                            className="me-2"
                                        />
                                        {rule.is_active ? "Actif" : "Inactif"}
                                    </div>
                                </td>
                                <td className="text-center">
                                    <div className="d-flex gap-2 justify-content-center">
                                        <Button 
                                            size="sm"
                                            variant="link"
                                            style={{ color: '#007BFF', padding: 0 }}
                                            onClick={() => handleOpenModal(rule)}
                                            title="Modifier"
                                        >
                                            <FontAwesomeIcon icon={faEdit} />
                                        </Button>
                                        <Button 
                                            size="sm"
                                            variant="link"
                                            style={{ color: '#DC3545', padding: 0 }}
                                            onClick={() => deleteRule(rule.id)}
                                            title="Supprimer"
                                        >
                                            <FontAwesomeIcon icon={faTrashAlt} />
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
                {rules.length === 0 && (
                    <div className="text-center py-5">
                        <FontAwesomeIcon icon={faBan} className="text-muted mb-3" size="2x" />
                        <p className="fw-bold mb-0">Aucun blocage actif</p>
                        <p className="text-muted small">Tous les numéros sont ouverts par défaut.</p>
                    </div>
                )}
            </div>

            {/* Reorganized Editor Modal */}
            <Modal show={openModal} onHide={handleCloseModal} size="xl" centered style={{ borderRadius: '4px' }}>
                <Modal.Header closeButton className="border-bottom-0 pb-0">
                    <Modal.Title style={{ fontWeight: 'bold', color: '#333' }}>
                        {isEditing ? `Modifier ${mode === 'blocage' ? 'le blocage' : 'la limite'}` : `Nouveau ${mode === 'blocage' ? 'blocage' : 'plafond'} groupé`}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-3">
                    {/* Top Row: Context & Parameters */}
                    <div className="row bg-light p-3 mb-4 mx-0 border rounded" style={{ borderRadius: '4px' }}>
                        {!isEditing && targetLevel !== 'GENERAL' && targetLevel !== 'TIRAGE' ? (
                            <>
                                <div className="col-md-4 mb-2 mb-md-0">
                                    <Form.Label className="small fw-bold">{targetLevel === 'AGENT' ? 'Agent' : 'Succursale'}</Form.Label>
                                    <Form.Select disabled={isEditing}
                                        value={targetLevel === 'AGENT' ? formData.agent : formData.branch}
                                        onChange={(e) => handleChange(targetLevel === 'AGENT' ? 'agent' : 'branch', e.target.value)}
                                        style={{ borderRadius: '4px' }}
                                    >
                                        <option value="">Tous les {targetLevel === 'AGENT' ? 'agents' : 'succursales'}</option>
                                        {targets.map(t => (
                                            <option key={t.id} value={t.id}>
                                                {targetLevel === 'AGENT' ? (t.username || `${t.first_name} ${t.last_name}`) : t.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </div>
                                <div className="col-md-4 mb-2 mb-md-0">
                                    <Form.Label className="small fw-bold">Tirage cible</Form.Label>
                                    <Form.Select value={formData.draw || ""} onChange={e => handleChange('draw', e.target.value)} style={{ borderRadius: '4px' }}>
                                        {targetLevel !== 'TIRAGE' && <option value="">Tous les Tirages</option>}
                                        {draws.map(d => {
                                            const drawId = d.draw?.id || d.id;
                                            const drawName = d.draw?.name || d.name;
                                            return <option key={drawId} value={drawId}>{drawName}</option>
                                        })}
                                    </Form.Select>
                                </div>
                                <div className="col-md-4">
                                    <Form.Label className="small fw-bold">Type de Jeu</Form.Label>
                                    <Form.Select value={formData.game_type} onChange={e => handleChange('game_type', e.target.value)} style={{ borderRadius: '4px' }}>
                                        <option value="BORLETTE">Borlette</option>
                                        <option value="MARIAGE">Mariage</option>
                                        <option value="LOTO3">Loto 3</option>
                                        <option value="LOTO4">Loto 4</option>
                                        <option value="LOTO5">Loto 5</option>
                                    </Form.Select>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="col-md-6 mb-2 mb-md-0">
                                    <Form.Label className="small fw-bold">Tirage cible</Form.Label>
                                    <Form.Select value={formData.draw || ""} onChange={e => handleChange('draw', e.target.value)} style={{ borderRadius: '4px' }}>
                                        {targetLevel !== 'TIRAGE' && <option value="">Tous les Tirages</option>}
                                        {draws.map(d => {
                                            const drawId = d.draw?.id || d.id;
                                            const drawName = d.draw?.name || d.name;
                                            return <option key={drawId} value={drawId}>{drawName}</option>
                                        })}
                                    </Form.Select>
                                </div>
                                <div className="col-md-6">
                                    <Form.Label className="small fw-bold">Type de Jeu</Form.Label>
                                    <Form.Select value={formData.game_type} onChange={e => handleChange('game_type', e.target.value)} style={{ borderRadius: '4px' }}>
                                        <option value="BORLETTE">Borlette</option>
                                        <option value="MARIAGE">Mariage</option>
                                        <option value="LOTO3">Loto 3</option>
                                        <option value="LOTO4">Loto 4</option>
                                        <option value="LOTO5">Loto 5</option>
                                    </Form.Select>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Bottom Split Row: Mode/Summary vs Interactive Selector */}
                    <div className="row">
                        {/* Left Side: Parameters & Summary */}
                        <div className="col-md-5 d-flex flex-column" style={{ gap: '15px' }}>
                            <Card className="border p-3" style={{ borderRadius: '4px', backgroundColor: '#FFF' }}>
                                {mode === 'limite' ? (
                                    <div className="pt-1">
                                        <Form.Label className="small fw-bold">Exposition Maximum (HTG)</Form.Label>
                                        <Form.Control 
                                            type="number" 
                                            placeholder="0" 
                                            value={formData.limit_amount} 
                                            onChange={e => handleChange('limit_amount', e.target.value)}
                                            className="fw-bold text-end"
                                            style={{ borderRadius: '4px' }}
                                        />
                                    </div>
                                ) : (
                                    <div className="d-flex justify-content-center align-items-center text-muted small py-3" style={{ height: '100%' }}>
                                        <FontAwesomeIcon icon={faBan} className="me-2" /> La vente sera complètement bloquée.
                                    </div>
                                )}
                            </Card>

                            {/* Selection Summary Box */}
                            <Card className="border p-3 flex-grow-1" style={{ borderRadius: '4px', minHeight: '150px' }}>
                                <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-2">
                                    <Form.Label className="small fw-bold mb-0">
                                        {isEditing ? "Numéro ciblé" : `Numéros sélectionnés (${selectedNumbers.length})`}
                                    </Form.Label>
                                    {!isEditing && selectedNumbers.length > 0 && (
                                        <Button variant="link" className="text-danger p-0 small" onClick={() => setSelectedNumbers([])} style={{ textDecoration: 'none', fontSize: '0.75rem' }}>
                                            Tout effacer
                                        </Button>
                                    )}
                                </div>

                                <div className="d-flex flex-wrap gap-1 align-content-start overflow-y-auto" style={{ maxHeight: '200px', gap: '6px' }}>
                                    {selectedNumbers.length === 0 ? (
                                        <div className="text-center w-100 py-4 text-muted small">
                                            Aucun numéro sélectionné. Utilisez la grille ou le champ de saisie à droite.
                                        </div>
                                    ) : (
                                        selectedNumbers.map(num => (
                                            <span
                                                key={num}
                                                className="badge bg-light text-primary border d-inline-flex align-items-center px-2 py-1.5"
                                                style={{ borderRadius: '3px', fontSize: '0.8rem', fontWeight: 'bold' }}
                                            >
                                                {formData.game_type === 'MARIAGE' ? formatMariageDisplay(num) : num}
                                                {!isEditing && (
                                                    <Button
                                                        variant="link"
                                                        className="p-0 ms-2 text-danger fw-bold"
                                                        style={{ textDecoration: 'none', fontSize: '0.85rem', lineHeight: 1 }}
                                                        onClick={() => setSelectedNumbers(prev => prev.filter(n => n !== num))}
                                                    >
                                                        &times;
                                                    </Button>
                                                )}
                                            </span>
                                        ))
                                    )}
                                </div>
                            </Card>
                        </div>

                        {/* Right Side: Interactive Keyboard & Grid */}
                        <div className="col-md-7 border-start ps-md-4 mt-3 mt-md-0">
                            {!isEditing ? (
                                <div>
                                    <Form.Label className="small fw-bold mb-2">Saisie et Sélection des numéros</Form.Label>
                                    <InputGroup className="mb-3">
                                        <Form.Control
                                            ref={manualInputRef}
                                            placeholder="Saisir numéro(s) séparés par des espaces ou virgules..."
                                            style={{ borderRadius: '4px 0 0 4px' }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleAddManualNumbers();
                                                }
                                            }}
                                        />
                                        <Button 
                                            variant="primary"
                                            onClick={handleAddManualNumbers}
                                            style={{ borderRadius: '0 4px 4px 0' }}
                                        >
                                            Ajouter
                                        </Button>
                                    </InputGroup>

                                    {formData.game_type === 'BORLETTE' ? (
                                        <>
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <div className="d-flex gap-1">
                                                    <Button size="sm" variant="outline-danger" onClick={() => quickSelect('doubles')} style={{ fontSize: '0.7rem', padding: '3px 10px', borderRadius: '4px' }}>Paires</Button>
                                                    <Button size="sm" variant="outline-dark" onClick={() => quickSelect('even')} style={{ fontSize: '0.7rem', padding: '3px 10px', borderRadius: '4px' }}>Pairs</Button>
                                                    <Button size="sm" variant="outline-dark" onClick={() => quickSelect('odd')} style={{ fontSize: '0.7rem', padding: '3px 10px', borderRadius: '4px' }}>Impairs</Button>
                                                </div>
                                                <Button size="sm" variant="outline-primary" onClick={() => quickSelect('all')} style={{ fontSize: '0.7rem', padding: '3px 10px', borderRadius: '4px' }}>Sélectionner Tout</Button>
                                            </div>
                                            <div className="bg-light p-2 border rounded" style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: '4px', borderRadius: '4px' }}>
                                                {[...Array(100)].map((_, i) => {
                                                    const s = i.toString().padStart(2, '0');
                                                    const isSelected = selectedNumbers.includes(s);
                                                    return (
                                                        <Button
                                                            key={i}
                                                            variant={isSelected ? "danger" : "white"}
                                                            size="sm"
                                                            className="p-0 border fw-bold"
                                                            style={{ fontSize: '0.75rem', height: '32px', borderRadius: '3px' }}
                                                            onClick={() => toggleNumber(i)}
                                                        >
                                                            {s}
                                                        </Button>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    ) : formData.game_type === 'MARIAGE' ? (
                                        <MariageComboGrid
                                            step={mariageStep}
                                            firstNumber={mariageFirst}
                                            selectedCombos={selectedNumbers}
                                            onPick={handleMariagePick}
                                            onCancel={cancelMariageStep}
                                            onRemove={(combo) => setSelectedNumbers(prev => prev.filter(n => n !== combo))}
                                            formatDisplay={formatMariageDisplay}
                                        />
                                    ) : (
                                        <div className="bg-light p-4 border rounded text-center text-muted small" style={{ borderRadius: '4px' }}>
                                            Entrez les numéros dans le champ ci-dessus.
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="bg-light border rounded p-5 text-center d-flex flex-column justify-content-center align-items-center h-100" style={{ borderRadius: '4px' }}>
                                    <div className="text-muted small mb-2 fw-bold">MODIFICATION EN COURS DU NUMÉRO</div>
                                    <h1 className="fw-black mb-0 text-danger" style={{ fontSize: '4.5rem', letterSpacing: '2px' }}>{formData.number}</h1>
                                    <span className="badge bg-secondary text-uppercase mt-3 px-3 py-2">{formData.game_type}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {validationError && (
                        <Alert variant="danger" className="mt-3 py-2 small" style={{ borderRadius: '4px' }}>
                            {validationError}
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-top-0 pt-0">
                    <Button variant="link" className="text-muted" onClick={handleCloseModal} style={{ textDecoration: 'none' }}>Annuler</Button>
                    <Button 
                        variant="primary"
                        onClick={saveRule} 
                        disabled={saving}
                        style={{ borderRadius: '4px', fontWeight: '500', padding: '8px 24px' }}
                    >
                        {saving ? "Application..." : isEditing ? "Sauvegarder" : `Appliquer le blocage (${selectedNumbers.length})`}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}


function MariageComboGrid({ step, firstNumber, selectedCombos, onPick, onCancel, onRemove, formatDisplay }) {
    return (
        <div>
            <div className="d-flex align-items-center gap-2 px-1 mb-2">
                <div className={`d-flex align-items-center justify-content-center rounded-circle text-[10px] font-black ${
                    step === 1 ? 'bg-primary text-white' : 'bg-light text-muted'
                }`} style={{ width: '22px', height: '22px' }}>1</div>
                <span className={`small fw-bold ${step === 1 ? 'text-primary' : 'text-muted'}`} style={{ fontSize: '0.75rem' }}>1er numéro</span>
                <div className="flex-grow-1 border-top" />
                <div className={`d-flex align-items-center justify-content-center rounded-circle text-[10px] font-black ${
                    step === 2 ? 'bg-danger text-white' : 'bg-light text-muted'
                }`} style={{ width: '22px', height: '22px' }}>2</div>
                <span className={`small fw-bold ${step === 2 ? 'text-danger' : 'text-muted'}`} style={{ fontSize: '0.75rem' }}>2ème numéro</span>
            </div>

            {step === 2 && firstNumber && (
                <div className="d-flex align-items-center gap-2 px-1 my-2">
                    <div className="d-flex align-items-center gap-2 px-2 py-1 bg-light rounded border">
                        <span className="small text-primary fw-bold" style={{ fontSize: '0.7rem' }}>1er :</span>
                        <span className="fw-bold text-primary" style={{ fontSize: '0.85rem' }}>{firstNumber}</span>
                    </div>
                    <span className="text-muted fw-bold">×</span>
                    <span className="text-danger" style={{ fontSize: '0.75rem' }}>Choisissez le 2ème</span>
                    <Button
                        onClick={onCancel}
                        size="xs"
                        variant="light"
                        className="ms-auto"
                        style={{ padding: '2px 8px', fontSize: '0.65rem' }}
                    >Annuler</Button>
                </div>
            )}

            <div className="bg-light p-2 border rounded" style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: '4px', borderRadius: '4px' }}>
                {[...Array(100)].map((_, i) => {
                    const s = i.toString().padStart(2, '0');
                    const isFirst = step === 2 && firstNumber === s;
                    return (
                        <Button
                            key={i}
                            variant={isFirst ? "primary" : "white"}
                            size="sm"
                            className="p-0 border fw-bold"
                            style={{ fontSize: '0.75rem', height: '32px', borderRadius: '3px' }}
                            onClick={() => onPick(i)}
                        >
                            {s}
                        </Button>
                    );
                })}
            </div>
        </div>
    );
}

export default BlocageBouleTable;
